import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import classNames from 'classnames'

import Form from '../../forms/layout-form'
import PasswordInput from '../../forms/password-input'
import ModalContainer from '../../modal-container'
import Button from '../../button'
import { getAccountIdFromState } from '../../../selectors/session'
import { setAccountPasswordTimestamp } from '../../../actions'
import { FORM_MESSAGES, MODALS } from '../../../constants'
import { useQueryAndLoading } from '../../../hooks/use-query-and-loading'
import VALIDATE_PASSWORD_QUERY from '../../../../graphql/queries/validate-password.gql'
import styles from './enter-account-password.css'

const isPasswordInvalid = result => result.data?.validatePassword === false

/**
 * Modal for validating the account password before unlocking protected
 * account actions, with links to cancel or start password recovery.
 */
function EnterAccountPasswordModal() {
  const dispatch = useDispatch()
  const history = useHistory()
  const accountId = useSelector(getAccountIdFromState)
  const [passwordValidated, setPasswordValidated] = useState(null)
  // The mounted ref prevents stale password validation results from updating
  // component state after the modal has closed.
  const isMounted = useRef(false)
  const successTimeout = useRef(null)
  const cancelTimeout = useRef(null)
  const queryAndLoading = useQueryAndLoading(VALIDATE_PASSWORD_QUERY)
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: { password: '' },
    mode: 'onChange'
  })

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      clearTimeout(successTimeout.current)
      clearTimeout(cancelTimeout.current)
    }
  }, [])

  const onSubmit = async ({ password }) => {
    if (isSubmitting || passwordValidated) return
    setPasswordValidated(null)

    try {
      const { data } = await queryAndLoading(
        'validatePasswordQuery',
        {
          variables: { password },
          fetchPolicy: 'network-only'
        },
        isPasswordInvalid
      )
      // The mounted ref prevents stale password validation results from updating
      // component state after the modal has closed.
      if (!isMounted.current) return

      setPasswordValidated(data.validatePassword)
      if (data.validatePassword) {
        // Keep the success feedback visible before unlocking the account screen.
        successTimeout.current = setTimeout(() => {
          dispatch(setAccountPasswordTimestamp(new Date(), accountId))
        }, 2000)
      }
    } catch (error) {
      if (isMounted.current) setPasswordValidated(false)
    }
  }

  const handleCancel = (event) => {
    // The shared Button defaults to submit; cancellation must not submit a password.
    event.preventDefault()
    clearTimeout(successTimeout.current)
    reset()
    setPasswordValidated(null)
    // Allow the cleared field to render before navigation (required for Edge).
    cancelTimeout.current = setTimeout(() => history.push('/'), 0)
  }

  const handleIForgotMyPassword = () => {
    clearTimeout(successTimeout.current)
    history.push(`/?modal=${MODALS.qsParams.passwordForgotten}`)
  }

  const errorMsg = "Sorry! We don't recognise this password."

  return (
    <ModalContainer onRequestClose={handleCancel}>
      <div className={styles.wrapper}>
        <h2
          className={styles.header}
          data-e2e="my-account-modal-password-text"
        >
          Enter your account password

        </h2>

        <div
          className={styles.message}
        >
          Please enter your account password to continue
        </div>

        <Form
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit(onSubmit)}
          formClassName={styles.form}
        >
          <div>
            <Controller
              name="password"
              control={control}
              rules={{ required: FORM_MESSAGES.required }}
              render={({ field }) => (
                <PasswordInput
                  label="Password"
                  name={field.name}
                  value={field.value}
                  onChange={(event) => {
                    setPasswordValidated(null)
                    field.onChange(event)
                  }}
                  onBlur={field.onBlur}
                  setRef={field.ref}
                  disabled={isSubmitting || passwordValidated === true}
                  warning={errors.password?.message || (passwordValidated === false ? errorMsg : '')}
                  isValid={passwordValidated}
                  inputClass={classNames({
                    [styles.validInput]: passwordValidated
                  })}
                  data-e2e="account-password"
                />
              )}
            />
            {
              passwordValidated &&
              <div className={styles.thanks}>Thanks!</div>
            }
            <div className={styles.iForgotMyPassword}>
              <span onClick={handleIForgotMyPassword}>
                I forgot my password
              </span>
            </div>
          </div>
          <div className={styles.buttons}>
            <Button
              wrapperClassName={styles.buttonWrapper}
              className={styles.button}
              text="OK"
              disabled={isSubmitting || passwordValidated === true}
              e2eIdentifier="OK-button"
              lightBGPrimary
            />
            <Button
              e2eIdentifier="cancel-button"
              wrapperClassName={styles.buttonWrapper}
              className={styles.button}
              text="Cancel"
              disabled={isSubmitting}
              secondary
              onClick={handleCancel}
            />
          </div>
        </Form>
      </div>
    </ModalContainer>
  )
}

export default EnterAccountPasswordModal
